import React, { useEffect } from "react";
import { QuestionProps } from "../survey";
import Likert, { LikertFollowUpMultiSelectAnswer, Scale as LikertScale } from "./components/likert";
import useRotatingQuestionIndex from "./hooks/useRotatingQuestion";
import { captureError } from "../../../utils/capture_error";

export interface Answer {
  prompt: string;
  value: LikertScale;
  qid: string;
  followUpResponse?: string | LikertFollowUpMultiSelectAnswer;
  followUpQid?: string;
}

export interface Customizations {
  rotatingQuestions: {
    prompt: string;
    qid: string;
    followUpLowScore: boolean;
    followUpPrompt?: string;
    followUpQid?: string;
    description?: string;
  }[];
}

export default function RotatingLikert({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
}: QuestionProps<Answer, Customizations>) {
  const { fetching, questionIndex } = useRotatingQuestionIndex({
    numberOfQuestions: customizations.rotatingQuestions.length,
  });
  const answerIsValid = typeof currentAnswer?.value !== "undefined";
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  if (fetching || questionIndex === null) return <div>Loading...</div>;
  if (typeof questionIndex === "undefined") {
    captureError(new Error(`Question index is undefined for rotating likert`));
    throw new Error("The question index is undefined");
  }
  const likertQuestion = customizations.rotatingQuestions[questionIndex];

  return (
    <div className="multi-select">
      {likertQuestion.followUpLowScore ? (
        <Likert
          value={currentAnswer ? currentAnswer.value : undefined}
          prompt={likertQuestion.prompt}
          description={likertQuestion.description}
          onChangeValue={(newValue) =>
            updateAnswer(
              currentAnswer
                ? { ...currentAnswer, value: newValue }
                : {
                    prompt: likertQuestion.prompt,
                    qid: likertQuestion.qid,
                    followUpQid: likertQuestion.followUpQid,
                    value: newValue,
                  },
            )
          }
          followUpLowScore={true}
          followUpPrompt={likertQuestion.followUpPrompt}
          followUpResponse={
            currentAnswer && currentAnswer.followUpResponse ? currentAnswer.followUpResponse : ""
          }
          onChangeLowFollowUp={(newResponse) => {
            if (!currentAnswer)
              throw new Error("Attempted to update NPS low score follow up without a score");
            updateAnswer({ ...currentAnswer, followUpResponse: newResponse });
          }}
        />
      ) : (
        <Likert
          value={currentAnswer ? currentAnswer.value : undefined}
          prompt={likertQuestion.prompt}
          description={likertQuestion.description}
          onChangeValue={(newValue) =>
            updateAnswer(
              currentAnswer
                ? { ...currentAnswer, value: newValue }
                : { prompt: likertQuestion.prompt, qid: likertQuestion.qid, value: newValue },
            )
          }
          followUpLowScore={false}
        />
      )}
    </div>
  );
}
